import {
  Signal,
  SignalDataWithKey,
  SortingOrder,
  LanguageNameSpaces,
} from "@shared/types";
import React, { useContext, useMemo } from "react";
import { ColumnType } from "@shared/common/CardsList/types";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import { checkSymbolSearch } from "@shared/utils/misc";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import { SignalColumn } from "@shared/features/signals/common/types";
import { typography } from "@shared/common/Themes/typography";
import { SignalsEmptyStateProps } from "features/signals/common/types";
import SignalsEmptyState from "features/signals/SignalsEmptyState";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import FlatList from "common/Lists/FlatList";
import SignalCard from "features/signals/SignalsCards/SignalCard";
import { SIGNAL_CARD_COLUMN_SPANS } from "features/signals/common/constants";
import { useSortSignalsCards } from "@shared/features/signals/common/hooks";
import useExpandedCardsSorters from "common/CardSorters/useExpandedCardsSorters";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { SignalActionsContext } from "@shared/features/signals/common/SignalActions.context";
import { CardsSelectorContext } from "@shared/common/CardsList/CardsSelector.context";
import useCardsListSelector from "@shared/common/CardsList/useCardsListSelector";
import BulkActions from "common/Table/BulkActions";
import { openMessage } from "utils/misc";
import PhoneSignalsList from "features/signals/NewLook/PhoneSignalsList.tsx";

interface SignalsCardsListProps {
  data?: Signal[];
  loading: boolean;
  isOpenSignals?: boolean;
  renderTopContainer?: () => React.JSX.Element;
  emptyStateProps: SignalsEmptyStateProps;
  refreshing?: boolean;
  onRefresh?: () => void;
}

const SignalsCardsList = ({
  data,
  loading,
  isOpenSignals,
  renderTopContainer,
  emptyStateProps,
  refreshing,
  onRefresh,
}: SignalsCardsListProps) => {
  const {
    selectedSignalIDs,
    setSelectedSignalIDs,
    isBulkMode,
    setIsBulkMode,
    exitBulkMode,
    actionButtonsData,
    showEditSignalActions,
  } = useContext(SignalActionsContext)!;

  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const theme = useTheme();
  usePageWrapper({ showSearch: true });
  const { search } = useContext(PageWrapperContext);
  const { signalColumnToSorter, defaultSortFunction } = useSortSignalsCards();

  const dataWithKey: SignalDataWithKey[] = useMemo(() => {
    return data?.map((signal) => ({ ...signal, key: signal.signal_id })) || [];
  }, [data]);

  const filteredData = useMemo(() => {
    return dataWithKey.filter((signal) =>
      checkSymbolSearch(signal.symbol, search.value)
    );
  }, [dataWithKey, search]);

  const { isAllRowsSelected, getCheckboxProps, onSelectAll, onSelectRow } =
    useCardsListSelector<SignalDataWithKey, number>({
      selectedRows: selectedSignalIDs,
      setSelectedRows: setSelectedSignalIDs,
      getKey: (signal: SignalDataWithKey) => signal.signal_id,
      allRows: dataWithKey,
      filteredRows: filteredData,
      openMessage,
    });

  const columns: ColumnType<SignalDataWithKey>[] = [
    {
      key: SignalColumn.TIME,
      sorter: signalColumnToSorter[SignalColumn.TIME],
      title: (selected) => (
        <ClockCircleOutlined
          style={{
            fontSize: 12,
            color: selected ? theme.colors.basic : theme.colors.secondaryGrey,
          }}
        />
      ),
      defaultSortOrder: SortingOrder.DESCEND,
      isPrefix: true,
    },
    {
      key: SignalColumn.SYMBOL,
      sorter: signalColumnToSorter[SignalColumn.SYMBOL],
      span: SIGNAL_CARD_COLUMN_SPANS[0],
      title: (selected) => (
        <div>
          <CardSorterTitle
            $sorted={selected}
            style={{
              fontSize: isOpenSignals
                ? typography.fontSizes.XS
                : typography.fontSizes.S,
            }}
          >
            {t("cards.columns.symbol")}
          </CardSorterTitle>
          {isOpenSignals && (
            <CardSorterTitle>
              ({t("cards.columns.current_price")})
            </CardSorterTitle>
          )}
        </div>
      ),
    },
    {
      key: SignalColumn.POTENTIAL_PROFIT,
      sorter: signalColumnToSorter[SignalColumn.POTENTIAL_PROFIT],
      span: SIGNAL_CARD_COLUMN_SPANS[1],
      title: (selected) => (
        <div>
          <CardSorterTitle $sorted={selected}>
            {t("cards.columns.potential_profit")}
          </CardSorterTitle>
          <CardSorterTitle>({t("cards.columns.risk_reward")})</CardSorterTitle>
        </div>
      ),
    },
    {
      key: SignalColumn.PROGRESS,
      sorter: signalColumnToSorter[SignalColumn.PROGRESS],
      span: SIGNAL_CARD_COLUMN_SPANS[2],
      title: (selected) => (
        <CardSorterTitle
          $sorted={selected}
          style={{ fontSize: typography.fontSizes.S }}
        >
          {t("cards.columns.progress")}
        </CardSorterTitle>
      ),
    },
    {
      title: "",
      key: "action",
    },
  ];

  const cardsContextValue = useMemo(() => {
    if (!showEditSignalActions || !setIsBulkMode) return undefined;
    return {
      isEditMode: !!isBulkMode,
      setIsEditMode: (signal: SignalDataWithKey) => {
        setIsBulkMode(true);
        onSelectRow(signal, true);
      },
      getCheckboxProps,
    };
  }, [isBulkMode, getCheckboxProps, showEditSignalActions]);

  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useExpandedCardsSorters({
      data: filteredData,
      columns,
      defaultSortFunction,
    });

  const renderContent = ({ item }: { item: SignalDataWithKey }) => {
    return (
      <SignalCard
        signalData={item}
        isExpanded={isExpanded(item?.key)}
        toggleExpanded={toggleExpanded}
      />
    );
  };

  const emptyState = (
    <SignalsEmptyState
      {...emptyStateProps}
      isSearch={!!search.value.length}
      size={100}
    />
  );

  return (
    <CardsSelectorContext.Provider value={cardsContextValue}>
      {theme.isNewLook ? (
        <PhoneSignalsList
          data={filteredData}
          loading={loading}
          isOpenSignals={isOpenSignals}
          renderTopContainer={renderTopContainer}
          emptyState={emptyState}
          refreshing={refreshing}
          onRefresh={onRefresh}
        />
      ) : (
        <FlatList
          data={sortedData || []}
          gap={12}
          loading={loading}
          refreshing={refreshing}
          onRefresh={onRefresh}
          renderContent={renderContent}
          renderTopContainer={() => (
            <>
              {renderTopContainer?.()}
              <div style={{ marginBottom: 12 }}>{cardsSorters}</div>
            </>
          )}
          emptyState={emptyState}
          listStyle={{ paddingBottom: 16 }}
          wrapperStyle={{ padding: 16 }}
          hasActionButton={true}
        />
      )}
      {isBulkMode && actionButtonsData && (
        <BulkActions
          actionButtonsData={actionButtonsData}
          unselectAll={() => setSelectedSignalIDs?.([])}
          selectAll={onSelectAll}
          numSelected={selectedSignalIDs?.length || 0}
          isAllSelected={isAllRowsSelected}
          onCancel={exitBulkMode}
        />
      )}
    </CardsSelectorContext.Provider>
  );
};

export default SignalsCardsList;

const CardSorterTitle = styled(Text)<{ $sorted?: boolean }>`
  color: ${(p) =>
    p.$sorted ? p.theme.colors.primaryGrey : p.theme.colors.secondaryGrey};
  font-weight: ${(p) =>
    p.$sorted
      ? p.theme.typography.fontWeights.SEMI_BOLD
      : p.theme.typography.fontWeights.NORMAL};
  font-size: ${(p) => p.theme.typography.fontSizes.XS};
`;
